import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';
import { Place } from './place';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

import { PlaceType } from '../types';
import { I18nValueType } from '../types';

const _schema = {
    name: 'PRICETIME',
    fields: [
        { name: 'status', type: 'string', default: "'AC'" },
        { name: 'place', type: 'objid' },
        { name: 'name', type: 'objid' },
        { name: 'allday', type: 'boolean' },
        { name: 'weekdays', type: 'string' },
        { name: 'ini', type: 'string' },
        { name: 'end', type: 'string' },
        { name: 'timezone', type: 'string' },
        { name: 'sort', type: 'number' },
    ],
    relate: [
        { direction: '>', target: 'PLACE', by: 'place', name: 'place', reason: 'place', child: false, class: PlaceType },
        { direction: '>', target: 'I18NVALUE', by: 'name', name: 'name', reason: 'name', child: true, class: I18nValueType },
    ]
} as const;

const _PriceTimeClass = CreateObject(_schema);
if (!_PriceTimeClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class PriceTime extends _PriceTimeClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): PriceTime {
        return this._Copy(store) as PriceTime;
    }

    /** Changelog rows lack parent 1:N lists; register on link (see Preselect / TicketProduct). */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (this.CopyOf || !this.Commited){
            return;
        }

        if (relation === 'place' && related instanceof Place){
            if (related === this.place){
                related.AddPricetime(this);
            }
        }
    }

    /****************************/
    /* CUSTOM MEMBERS           */
    /****************************/

    get status(): string | null {
        return this['__base__status'];
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;
        }

        if (this.status == value){
            return;
        }

        this['__base__status'] = value;

        const _place = this.place;
        if (!_place){
            return;
        }

        if ((this.status == 'DE') && !this.Commited){
            _place.DelPricetime(this);
        }
        else {
            _place.DoRefresh(this.table);
        }
    }

    /**
     * List order among place periods (lower wins on overlap). Changing `sort`
     * notifies the parent place so `PlaceView.PriceTimes` rebuilds sorted.
     */
    get sort(): number | null {
        return this['__base__sort'];
    }

    set sort(value: number | null){
        if (this.sort === value){
            return;
        }

        this['__base__sort'] = value;
        this.place?.DoRefresh(this.table);
    }

    get IsValid(): boolean {
        return (!!this.status && (this.status != 'DE'));
    }

    /**
     * Whether this period is active at `at`: status AC; if `allday`, always active;
     * otherwise weekday + `[ini, end)` with end exclusive. Same-day when `ini < end`;
     * overnight when `ini > end` (weekday is the start day). Uses `at` as-is;
     * does not apply the persisted `timezone` field.
     */
    IsActiveAt(at: Date): boolean {
        if (!this.IsValid || this.status !== 'AC'){
            return false;
        }

        if (this.allday){
            return true;
        }

        const _weekdays = this.weekdays;
        if (!_weekdays || _weekdays.length < 7){
            return false;
        }

        const _ini = this._MinutesFromMidnight(this.ini);
        const _end = this._MinutesFromMidnight(this.end);
        if (_ini === null || _end === null || _ini === _end){
            return false;
        }

        const _atMinutes = at.getHours() * 60 + at.getMinutes();
        const _jsDay = at.getDay();
        const _wd = _jsDay === 0 ? 6 : _jsDay - 1;

        if (_ini < _end){
            if (_weekdays.charAt(_wd) !== '1'){
                return false;
            }
            return (_atMinutes >= _ini) && (_atMinutes < _end);
        }

        if (_atMinutes >= _ini){
            return _weekdays.charAt(_wd) === '1';
        }

        if (_atMinutes < _end){
            const _prevWd = _wd === 0 ? 6 : _wd - 1;
            return _weekdays.charAt(_prevWd) === '1';
        }

        return false;
    }

    private _MinutesFromMidnight(hhmm: string | null): number | null {
        if (!hhmm){
            return null;
        }

        const _parts = hhmm.split(':');
        if (_parts.length < 2){
            return null;
        }

        const _hh = parseInt(_parts[0], 10);
        const _mm = parseInt(_parts[1], 10);
        if (isNaN(_hh) || isNaN(_mm)){
            return null;
        }

        return (_hh * 60) + _mm;
    }
}
